import {useState} from 'react';
import {useIntl} from "react-intl";
import {useSnackbar} from "notistack";

import {decideEnrichmentSuggestions} from "api/enrichment";

import {suggestedValues} from "../components/AttributeRow";
import {ENRICHMENT_STATE_FIELDS, ENRICHMENT_STATES, tabCountsOf, withTabCounts} from "../enrichmentConstants";

const sum = (results, count) => results.reduce((total, result) => total + count(result), 0);

function shiftedCounts(counts, rows, state, fields, results) {
    const sourceField = fields[state];
    const products = Object.fromEntries(rows.map((product) => [product.id, product]));
    const next = JSON.parse(JSON.stringify(counts));
    const shift = (countState, targetKeys, delta, productDelta) => {
        next[countState] = next[countState] ?? {products: 0, targets: {}};
        next[countState].products = Math.max(0, next[countState].products + productDelta);
        targetKeys.forEach((key) => {
            next[countState].targets[key] = Math.max(0, (next[countState].targets[key] ?? 0) + delta);
        });
    };

    results.filter((result) => 'done' === result.status && products[result.productId]).forEach((result) => {
        const product = products[result.productId];
        const applied = result.applied ?? [];
        const proposed = result.proposed ?? [];
        const decided = [...applied, ...(result.dismissed ?? []), ...proposed];
        const left = (product[sourceField] ?? []).filter((key) => !decided.includes(key));
        shift(state, decided, -1, 0 === left.length ? -1 : 0);
        if (applied.length > 0) {
            shift('applied', applied, 1, 0 === (product[fields.applied] ?? []).length ? 1 : 0);
        }
        if (proposed.length > 0) {
            shift('pending', proposed, 1, 0 === (product[fields.pending] ?? []).length ? 1 : 0);
        }
    });

    return next;
}

export function withDecision(product, fields, state, result) {
    const decided = [...(result.applied ?? []), ...(result.dismissed ?? []), ...(result.proposed ?? [])];
    const next = {...product, [fields[state]]: (product[fields[state]] ?? []).filter((key) => !decided.includes(key))};
    const add = (field, keys) => {
        if (keys.length > 0) {
            next[field] = [...new Set([...(next[field] ?? []), ...keys])];
        }
    };
    add(fields.applied, result.applied ?? []);
    add(fields.pending, result.proposed ?? []);

    return next;
}

export function shiftedTotals(totals, rows, state, fields, results) {
    const products = Object.fromEntries(rows.map((product) => [product.id, product]));
    const next = {...(totals ?? {})};
    const inAnyTab = (product, countState) => Object.values(ENRICHMENT_STATE_FIELDS)
        .some((tabFields) => (product[tabFields[countState]] ?? []).length > 0);

    results.filter((result) => 'done' === result.status && products[result.productId]).forEach((result) => {
        const product = products[result.productId];
        const after = withDecision(product, fields, state, result);
        ENRICHMENT_STATES.forEach((countState) => {
            const delta = Number(inAnyTab(after, countState)) - Number(inAnyTab(product, countState));
            next[countState] = Math.max(0, (next[countState] ?? 0) + delta);
        });
    });

    return next;
}

export default function useEnrichmentDecisions({tab, state, rows, setRows, setSelected, counts, setCounts, editedValues, currentUserName}) {
    const intl = useIntl();
    const {enqueueSnackbar} = useSnackbar();
    const [deciding, setDeciding] = useState(false);
    const fields = ENRICHMENT_STATE_FIELDS[tab] ?? null;
    const sourceField = 'applied' !== state ? fields?.[state] : null;

    const removeDecidedTargets = (results) => {
        if (!sourceField) {
            return;
        }
        const done = Object.fromEntries(results
            .filter((result) => 'done' === result.status)
            .map((result) => [result.productId, result]));

        setRows((current) => current
            .map((product) => (done[product.id] ? withDecision(product, fields, state, done[product.id]) : product))
            .filter((product) => product[sourceField].length > 0));
        setSelected({});
    };

    const notifyOutcome = (results) => {
        const skipped = sum(results, (result) => Object.keys(result.skipped ?? {}).length)
            + results.filter((result) => 'done' !== result.status).length;

        enqueueSnackbar(intl.formatMessage({id: 'enrichment.decision.outcome'}, {
            applied: sum(results, (result) => result.applied?.length ?? 0),
            proposed: sum(results, (result) => result.proposed?.length ?? 0),
            dismissed: sum(results, (result) => result.dismissed?.length ?? 0),
            skipped,
        }), {variant: skipped > 0 ? 'warning' : 'success'});
    };

    const updateAppliedValues = (results, product, targetKey, value) => {
        if (!appliedTarget(results, product, targetKey)) {
            return;
        }

        const decidedAt = new Date().toISOString();
        setRows((current) => current.map((row) => (row.id === product.id
            ? {
                ...row,
                enrichmentSuggestions: (row.enrichmentSuggestions ?? []).map((suggestion) => ('applied' === suggestion.status && suggestion.target === targetKey
                    ? {...suggestion, value, source: 'manual', decidedBy: currentUserName, decidedAt}
                    : suggestion)),
            }
            : row)));
    };

    const decide = (action, products, onDecided = null) => {
        if (0 === products.length) {
            return Promise.resolve(null);
        }

        setDeciding(true);

        return decideEnrichmentSuggestions(action, products)
            .then((response) => {
                const results = response.results ?? [];
                if (sourceField) {
                    setCounts({
                        ...withTabCounts(counts, tab, shiftedCounts(tabCountsOf(counts, tab), rows, state, fields, results)),
                        totals: shiftedTotals(counts.totals, rows, state, fields, results),
                    });
                }
                removeDecidedTargets(results);
                onDecided?.(results);
                notifyOutcome(results);

                return results;
            })
            .catch(() => {
                enqueueSnackbar(intl.formatMessage({id: 'enrichment.decision.error'}), {variant: 'error'});

                return null;
            })
            .finally(() => setDeciding(false));
    };

    const applyValue = (product, targetKey, value, onDecided = null) => (value
        ? decide('apply', [{id: product.id, targets: [targetKey], values: {[targetKey]: value}}], onDecided)
        : Promise.resolve(null));

    const appliedTarget = (results, product, targetKey) => (results ?? [])
        .some((result) => result.productId === product.id && 'done' === result.status && (result.applied ?? []).includes(targetKey));

    const pendingOf = (product) => product[fields?.pending] ?? [];

    return {
        deciding,
        applyProduct: (product, values) => decide('apply', [{id: product.id, targets: pendingOf(product), values}]),
        applyValues: (product, values) => decide('apply', [{id: product.id, targets: Object.keys(values), values}]),
        dismissTargets: (product, targetKeys) => decide('dismiss', [{id: product.id, targets: targetKeys}]),
        chooseValue: (product, targetKey, value) => applyValue(product, targetKey, value),
        changeAppliedValue: (product, targetKey, value) => applyValue(product, targetKey, value, (results) => updateAppliedValues(results, product, targetKey, value))
            .then((results) => appliedTarget(results, product, targetKey)),
        applySelection: (products) => decide('apply', products.map((product) => ({
            id: product.id,
            targets: pendingOf(product),
            values: editedValues.current[product.id] ?? suggestedValues(product),
        }))),
        dismissSelection: (products) => decide('dismiss', products.map((product) => ({
            id: product.id,
            targets: product[sourceField] ?? [],
        }))),
    };
}
